/**
 * PATH: apps/ai/src/pages/dashboard/WelcomeScreen.tsx
 * PURPOSE: Welcome screen with quick prompts and capability cards
 */

import {
  Sparkles, Zap, Target, AlertTriangle, PieChart, DollarSign,
  Shield, Brain, FileText, Mail, Calculator, FileSearch,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { getTimeBasedGreeting } from './utils';

const quickPrompts = [
  { icon: Target, title: "Today's Priorities", prompt: "What should I focus on today?", color: "from-teal-500 to-emerald-600" },
  { icon: AlertTriangle, title: "At-Risk Jobs", prompt: "Which jobs need immediate attention?", color: "from-red-500 to-orange-600" },
  { icon: PieChart, title: "Margin Ranking", prompt: "Rank jobs by margin performance", color: "from-blue-500 to-indigo-600" },
  { icon: DollarSign, title: "WIP & Cash Overview", prompt: "What's our current WIP and cash position?", color: "from-purple-500 to-pink-600" },
];

const capabilities = [
  { icon: FileText, title: "Generate Reports", description: "Create PDF summaries" },
  { icon: Mail, title: "Draft Emails", description: "Client communications" },
  { icon: Calculator, title: "Analyze Data", description: "Deep dive metrics" },
  { icon: FileSearch, title: "Find Documents", description: "Search & retrieve" },
];

export function WelcomeScreen({ onPromptClick }: { onPromptClick: (prompt: string) => void }) {
  const greeting = getTimeBasedGreeting();

  return (
    <div className="flex flex-col items-center justify-center py-8 w-full max-w-3xl mx-auto px-6 text-center">
      {/* Hero */}
      <div className="relative mb-8">
        <div className="absolute inset-0 w-24 h-24 rounded-2xl bg-gradient-to-br from-primary/30 to-primary/10 blur-xl animate-pulse" />
        <div className="relative w-20 h-20 rounded-2xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-2xl shadow-primary/30">
          <Sparkles className="w-10 h-10 text-primary-foreground" />
        </div>
        <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-xl bg-background shadow-lg flex items-center justify-center border-2 border-primary">
          <Zap className="w-4 h-4 text-amber-500 fill-amber-500" />
        </div>
      </div>

      <h1 className="text-3xl font-bold text-foreground mb-3">
        {greeting}! I'm <span className="text-primary">QuotechAI</span>
      </h1>
      <p className="text-muted-foreground mb-8 max-w-xl">
        Your construction finance intelligence assistant. I analyze your data in real-time
        to help you make better decisions.
      </p>

      {/* Quick Prompts */}
      <div className="w-full max-w-2xl mb-8">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-4">Quick Start</p>
        <div className="grid grid-cols-2 gap-3">
          {quickPrompts.map((item, i) => {
            const Icon = item.icon;
            return (
              <button
                key={i}
                onClick={() => onPromptClick(item.prompt)}
                className="group flex flex-col items-start gap-3 p-4 rounded-xl bg-card border border-border hover:border-primary/30 hover:shadow-lg transition-all text-left"
              >
                <div className={cn("p-2.5 rounded-xl bg-gradient-to-br text-white shadow-lg group-hover:scale-110 transition-transform", item.color)}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{item.title}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{item.prompt}</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Capabilities */}
      <div className="w-full max-w-2xl">
        <p className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider mb-4">I can help you</p>
        <div className="grid grid-cols-4 gap-2">
          {capabilities.map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="p-3 rounded-xl bg-accent/50 border border-border text-center">
                <div className="p-2 w-fit mx-auto rounded-lg bg-primary/10 mb-2">
                  <Icon className="w-4 h-4 text-primary" />
                </div>
                <p className="text-[11px] font-medium text-foreground">{item.title}</p>
                <p className="text-[10px] text-muted-foreground">{item.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trust badges */}
      <div className="mt-8 flex items-center justify-center gap-6 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1.5"><Shield className="w-3.5 h-3.5" /> Secure</span>
        <span className="flex items-center gap-1.5"><Zap className="w-3.5 h-3.5" /> Real-time</span>
        <span className="flex items-center gap-1.5"><Brain className="w-3.5 h-3.5" /> Context-aware</span>
      </div>
    </div>
  );
}
